/* The daily meals grid, as plain data: rows are items, columns are the days
   of the stay, a cell is how many of that item were served that day.

   Quantities only — the grid never works out money. Saving sends the changed
   cells; the server prices them and the bill comes back with the amounts. */

/* "2026-08-25" … "2026-08-27", inclusive. Worked in UTC so a timezone with
   a daylight-saving shift can never skip or repeat a day. */
export function datesBetween(from, to) {
  if (!from || !to || from > to) return [];
  const out = [];
  const d = new Date(`${from}T00:00:00Z`);
  const end = new Date(`${to}T00:00:00Z`);
  while (d <= end && out.length < 400) {
    out.push(d.toISOString().slice(0, 10));
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
}

/* Rows: every item marked for the grid that is still in use, plus any item
   that already has grid entries on this bill (so a since-retired item's
   entries stay visible and editable down to zero). */
export function gridRows(lines, items) {
  const cellsByItem = {};
  const savedByItem = {};
  const nameByItem = {};
  for (const l of lines) {
    if (l.kind !== "ITEM" || !l.serviceDate || l.chargeItemId == null) continue;
    (cellsByItem[l.chargeItemId] ||= {})[l.serviceDate] = l.quantity;
    (savedByItem[l.chargeItemId] ||= {})[l.serviceDate] = { unitPrice: l.unitPrice, taxable: l.taxable };
    nameByItem[l.chargeItemId] = l.description;
  }
  const rows = items
    .filter((i) => (i.inDailyGrid && i.isActive) || cellsByItem[i.id])
    .map((i) => ({ itemId: i.id, name: i.name, unitLabel: i.unitLabel, unitPrice: i.unitPrice,
      isActive: i.isActive, cells: cellsByItem[i.id] || {}, saved: savedByItem[i.id] || {} }));
  // Entries for an item that has since been deleted from the list.
  for (const id of Object.keys(cellsByItem)) {
    if (!rows.some((r) => String(r.itemId) === id)) {
      rows.push({ itemId: Number(id), name: nameByItem[id], unitLabel: "", unitPrice: null,
        isActive: false, cells: cellsByItem[id], saved: savedByItem[id] });
    }
  }
  return rows;
}

export const cellKey = (itemId, date) => `${itemId}|${date}`;

/* Blank counts as 0; otherwise a whole or two-decimal number. */
export function isQuantity(text) {
  const t = String(text ?? "").trim();
  return t === "" || (/^\d+(\.\d{1,2})?$/.test(t) && Number(t) <= 9999);
}

const asNumber = (v) => (String(v ?? "").trim() === "" ? 0 : Number(v));

export function cellValue(row, date, drafts) {
  const key = cellKey(row.itemId, date);
  if (key in drafts) return drafts[key];
  const saved = row.cells[date];
  return saved == null ? "" : String(Number(saved));
}

/* Edited cells that differ from what is saved, grouped for one request per
   day: {date: [{item_id, quantity}]}. Invalid cells are left out — the
   caller refuses to save while any exist. */
export function changesByDate(rows, drafts) {
  const out = {};
  for (const row of rows) {
    for (const [key, text] of Object.entries(drafts)) {
      const [itemId, date] = key.split("|");
      if (String(row.itemId) !== itemId || !isQuantity(text)) continue;
      if (asNumber(text) === asNumber(row.cells[date])) continue;
      (out[date] ||= []).push({ item_id: row.itemId, quantity: String(asNumber(text)) });
    }
  }
  return out;
}

export function rowTotal(row, dates, drafts) {
  const sum = dates.reduce((acc, d) => {
    const v = cellValue(row, d, drafts);
    return acc + (isQuantity(v) ? asNumber(v) : 0);
  }, 0);
  return Number(sum.toFixed(2));
}

/* The − / + buttons: one whole unit at a time, never below 0. A fractional
   count (0.5) steps to the next whole number in that direction. */
export function stepQuantity(text, delta) {
  const n = isQuantity(text) ? asNumber(text) : 0;
  const next = delta > 0 ? Math.floor(n) + 1 : Math.ceil(n) - 1;
  return next <= 0 ? "" : String(next);
}

/* ── Cost (before GST), in whole paise ──────────────────────────────────
   Display only. A saved, unedited cell shows the server's own amount; an
   edited one is estimated from the price it was entered at (or the item's
   current price for a new cell) until it is saved and the server prices it. */
export function toPaise(text) {
  const m = /^(\d+)(?:\.(\d{1,2}))?$/.exec(String(text ?? "").trim());
  if (!m) return 0;
  return Number(m[1]) * 100 + Number((m[2] || "0").padEnd(2, "0"));
}

export function cellCostPaise(row, date, drafts) {
  const key = cellKey(row.itemId, date);
  const saved = row.saved?.[date];
  if (!(key in drafts)) return saved ? toPaise(saved.taxable) : 0;
  const text = drafts[key];
  if (!isQuantity(text)) return 0;
  const price = toPaise(saved?.unitPrice ?? row.unitPrice);
  return Math.round(asNumber(text) * price);
}

export function rowCostPaise(row, dates, drafts) {
  return dates.reduce((acc, d) => acc + cellCostPaise(row, d, drafts), 0);
}

export function dayCostPaise(rows, date, drafts) {
  return rows.reduce((acc, r) => acc + cellCostPaise(r, date, drafts), 0);
}

export function hasEstimate(rows, drafts) {
  return Object.keys(drafts).some((key) => rows.some((r) => key.startsWith(`${r.itemId}|`)));
}

export const paiseToRupees = (paise) => (paise / 100).toFixed(2);
